import AddIcon from "@mui/icons-material/Add";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { useVizDispatch } from "@/shared/state/visualization";
import { addThreeDEntity } from "@/shared/state/visualization/actions";
import {
  buildEntityTree,
  type Entity,
  type EntityID,
  type EntityRegistry,
} from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./EntityList.module.css";
import { EntityRow } from "./EntityRow";
import { nextEntityId } from "./nextEntityId";

interface EntityListProps {
  /** Which entities exist, and their hierarchy. The authority on both. */
  registry: EntityRegistry;
  /** Components by entity id. Sparse: a structural entity has none. */
  components: Record<EntityID, Entity>;
  panelId: string;
  /** Whether a model may be authored here. See {@link MeshConfig}. */
  modelMeshEnabled: boolean;
  /** Whether a point cloud may be authored here. See {@link EntityConfig}. */
  pointCloudEnabled: boolean;
}

/**
 * The panel's entities as an indented tree, with a control to add another.
 *
 * Ordering and nesting come from `buildEntityTree`, so an entity with a dangling
 * parent, or one caught in a cycle, still appears — as a root. Hiding it would
 * leave the user no way to fix it.
 */
export function EntityList({
  registry,
  components,
  panelId,
  modelMeshEnabled,
  pointCloudEnabled,
}: EntityListProps) {
  const dispatch = useVizDispatch();
  const tree = React.useMemo(() => buildEntityTree(registry), [registry]);
  const entityIds = React.useMemo(() => Object.keys(registry), [registry]);

  // Start with the first entity open and the rest collapsed, then open anything
  // added afterwards. Held in a ref so re-rendering for an unrelated reason does
  // not re-collapse rows the user opened.
  const initiallyExpanded = React.useRef(new Set(entityIds.slice(0, 1)));
  const previousIds = React.useRef(entityIds);
  React.useEffect(
    function expandNewlyAddedEntities() {
      for (const id of entityIds) {
        if (!previousIds.current.includes(id)) {
          initiallyExpanded.current.add(id);
        }
      }
      previousIds.current = entityIds;
    },
    [entityIds],
  );

  const addEntity = React.useCallback(() => {
    dispatch(addThreeDEntity(panelId, { id: nextEntityId(registry) }));
  }, [dispatch, panelId, registry]);

  return (
    <>
      <ul className={styles.tree}>
        {tree.map((node) => (
          <EntityRow
            key={node.id}
            node={node}
            components={components}
            registry={registry}
            panelId={panelId}
            initiallyExpanded={initiallyExpanded.current}
            // `threeDPanelDataSchema` requires at least one entity.
            canDelete={entityIds.length > 1}
            modelMeshEnabled={modelMeshEnabled}
            pointCloudEnabled={pointCloudEnabled}
          />
        ))}
      </ul>
      <RobotoButton
        eventName="AddThreeDEntityClicked"
        eventProperties={{ panelId }}
        startIcon={<AddIcon />}
        size="small"
        onClick={addEntity}
      >
        Add entity
      </RobotoButton>
    </>
  );
}
